/**
 * §F7 dashboard — the KPI tile and the four `.grid2` cards.
 *
 * Every one of them is a pure function of the single `/dashboard` payload the page fetched
 * (Task 6's `fetchDashboard`) shaped by the pure helpers in `dashboard.ts` (Task 8). They
 * live here rather than in `index.tsx` so the page file stays the fetch, the three filters
 * and the report job, and so each card can be rendered on its own in a test against a
 * seeded envelope.
 *
 * Two things these cards deliberately do **not** do:
 *
 * 1. **No target is ever invented.** `targetRows` hands back `null` for a `no_target` row
 *    and `null`/0 % for an `awaiting` one; the card prints "No target set" and "0 % —
 *    awaiting first results" respectively, and `BarsH` draws no bar for either. At country
 *    scope the whole Target series is `null` (the server attaches a commitment only to a
 *    programme-wide answer), which is the shipped state of this card, not an edge case.
 * 2. **No programme reference line.** The Overview's targets chart draws one
 *    (`BarsH`'s `refLine`) because its rows are the four *countries* of one indicator, so a
 *    single dashed line is that one indicator's programme commitment. Here the rows are
 *    different indicators — producers, hectares, institutions, reforms — with different
 *    units on one shared axis, so a single vertical marker would be a number belonging to
 *    exactly one row drawn across all of them. The programme-level fact the line carries
 *    there is carried here by the `dashboard.insights.programmeLevel` note under the list.
 *
 * The insight sentences are the server's (`apps.meta.dashboard.INSIGHT_TEXT`, already
 * translated) — this module renders them and adds nothing to them.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { CSSProperties, ReactNode } from 'react';

import type { DashboardKpi, DashboardResponse, DashboardTargetRow } from '../../lib/api';
import { NO_VALUE, fmtNum, fmtPct } from '../../lib/format';
import { Icon } from '../../lib/icons';
import {
  ChartFrame,
  LineChart,
  Spark,
  axisTitle,
  chartH,
  chartW,
} from '../../components/charts';
import { downloadCsv, toCsv } from '../analysis/csv';
import {
  kpiDeltaClass,
  summariseInsights,
  targetGroups,
  targetRows,
  trendSeries,
  type DashboardOutcomeCard,
} from './dashboard';

/** The fill colour a group's progress bar falls back to without the page's own `color()`. */
const TARGET_COLORS = { achieved: '#2E7D32' };
/**
 * The trend chart's own box, chosen so that `h / (w + LINE_LABEL_GUTTER)` matches the bars
 * chart's `height / w` — the two cards share a grid row and a width, so equal viewBox ratios
 * mean equal rendered heights, set by the charts rather than by padding one of them out.
 */
const TREND_CHART_W = 540;
const TREND_CHART_H = 324;
/** Every tile's sparkline is the same box, pinned to the same corner (client, 2026-09-16). */
const SPARK_W = 64;
const SPARK_H = 24;

/**
 * One card's title row: the heading and its subtitle on the left, the card's own actions
 * (a CSV button, normally) right-aligned on the heading's baseline, and a hairline under
 * the pair — the `.ins-title`/`.ins-sub` grammar the insights blocks already use.
 *
 * It replaces a `float:right` action div, which is what let the CSV buttons sit at a
 * different height in every card and the chart start at a different left edge from the
 * title above it.
 */
function CardHead({
  title,
  sub,
  actions,
}: {
  title: ReactNode;
  /** Optional (client, 2026-10-08: drop the Targets card's own subtitle line outright). */
  sub?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="card-head">
      <div className="card-head-text">
        <h3>{title}</h3>
        {sub ? <div className="sub">{sub}</div> : null}
      </div>
      {actions ? <div className="cactions">{actions}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------------------ the KPI tile */

/**
 * The "View on map" button a tile and an outcome card share.
 *
 * A labelled button, not a bare glyph (client, 2026-09-22: *"change the view on map icon
 * rather to a view on map button, so that users can see easily"*): the glyph in the corner
 * was the one control on the dashboard a reader had to discover, and the map is the whole
 * point of a tile. The label stays `aria-label`-free because the text is now the label.
 */
function MapButton({
  id,
  onMap,
  className = 'kmap',
  testId = 'kpi-map',
}: {
  id: string;
  onMap: (id: string) => void;
  className?: string;
  /** `kpi-map` on a tile, `target-map` on a row — b21 sits in both lists. */
  testId?: string;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      className={className}
      data-testid={`${testId}-${id}`}
      title={t('dashboard.kpi.viewMap')}
      onClick={() => onMap(id)}
    >
      <Icon name="map" size={13} />
      <span className="kmap-text">{t('dashboard.kpi.viewMap')}</span>
    </button>
  );
}

/**
 * §1 section II — "measured" / "awaiting first results" as a pill above an outcome tile's
 * label.
 *
 * Deliberately a *state*, not a warning: an outcome indicator with no results yet is the
 * normal condition of a programme in its second year, and the seven tiles of section II
 * would read as seven faults if the pill were amber. Measured is the brand green, awaiting
 * is the page's own neutral ink on the line tint.
 */
function StatusPill({ status }: { status: 'measured' | 'awaiting' }) {
  const { t } = useTranslation();
  return (
    <span className={`kstatus kstatus-${status}`}>
      {status === 'measured' ? t('dashboard.status.measured') : t('dashboard.kpi.awaiting')}
    </span>
  );
}

/**
 * §1 section I — the 36 px progress ring on a contractual hero tile.
 *
 * The ring answers the one question the contractual section exists for: *how far along this
 * commitment are we*. The percentage is printed inside it, so nothing has to be read off an
 * arc, and the caption under it says what the percentage is **of** — "of target" normally,
 * "apportioned" where the server split a programme commitment across the scope
 * (`target_source === 'apportioned'`), which is a weaker claim and has to say so, and "of
 * IFDC target" where the commitment is IFDC's own country allocation
 * (`target_source === 'allocated'`, B.2.1 and C.2.1 since 6 Oct 2026).
 *
 * An `awaiting` row draws a **dashed, muted** ring rather than a 0 % filled one: a solid
 * ring at zero says "measured, and nothing achieved", which is a different and much worse
 * sentence than "the first results are not in".
 */
const RING_SIZE = 36;
const RING_R = 15;
const RING_C = 2 * Math.PI * RING_R;

function ProgressRing({
  pct,
  color,
  awaiting,
  caption,
}: {
  pct: number | null;
  color: string;
  awaiting: boolean;
  caption: string;
}) {
  const clamped = pct === null ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className={`kring${awaiting ? ' kring-awaiting' : ''}`}>
      <svg width={RING_SIZE} height={RING_SIZE} viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`} aria-hidden="true">
        <circle className="kring-track" cx={RING_SIZE / 2} cy={RING_SIZE / 2} r={RING_R} />
        {!awaiting && (
          <circle
            className="kring-arc"
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_R}
            stroke={color}
            strokeDasharray={`${(clamped / 100) * RING_C} ${RING_C}`}
            transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          />
        )}
      </svg>
      <span className="kring-pct">{awaiting ? NO_VALUE : `${Math.round(clamped)}%`}</span>
      <span className="kring-cap">{caption}</span>
    </div>
  );
}

export interface KpiTileProps {
  kpi: DashboardKpi;
  /** The indicator's domain colour, from `/meta/config` — the tile's 3 px top bar. */
  color: string;
  /** Index of the selected year in the Dashboard years (`DASHBOARD_YEARS`, 2024 on), for the sparkline's filled dot. */
  activeIdx: number;
  /**
   * "View on map" (client, 2026-09-15): maps this indicator in the explorer. Absent when the
   * page cannot offer it — a guest under a closed portal has no Indicators icon to land on
   * (`programmeInfoAllowed`), and a button that opened an empty pane would be a lie.
   */
  onMap?: (indicatorId: string) => void;
  /** The year the delta compares against; `null` when the selected year is the first. */
  prevYear: number | null;
  /**
   * The tile's position on the rail, for the entrance stagger alone (client, 2026-09-16:
   * "some neat effects"). It is written out as the `--i` custom property and read by
   * `.kpi`'s `animation-delay`; nothing about the tile's content or order depends on it,
   * and the whole animation is off under `prefers-reduced-motion`.
   */
  index?: number;
  /**
   * §1 section II — the status pill an *outcome* tile carries above its label: `measured`,
   * or "awaiting first results". The contractual heroes carry a progress ring instead (the
   * question there is "how far against the commitment"), and the thematic tiles carry
   * neither, so the pill is opt-in rather than derived from the KPI alone.
   */
  status?: 'measured' | 'awaiting';
  /** An extra class on the tile — `kpi-outcome` for section II's compact grid. */
  className?: string;
}

/**
 * `+16` / `−3` / `0`, with a real minus sign (U+2212) rather than a hyphen.
 *
 * The sign is taken from the **rounded** figure, not the raw one: a change of 0.02 % printed
 * at this precision is `0 %`, and `+0 %` is a sign attached to nothing. The tile's own
 * up/down colour still carries the direction.
 */
/*
  `round` and `signed` are exported for `baseline.ts` (final review, 2026-09-21): the survey
  cards print their deltas under the same rule as the KPI rail, and the two had a copy each.
  The rule they encode is this file's — a sign taken from the *printed* figure — so it stays
  here beside the tile that establishes it rather than moving to a neutral module.
*/
// eslint-disable-next-line react-refresh/only-export-components -- see above
export function round(n: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(n * factor) / factor;
}

/** Exported for `baseline.ts`: the survey cards print their deltas under the same rule. */
// eslint-disable-next-line react-refresh/only-export-components -- see `round` above
export function signed(n: number, decimals: number): string {
  const rounded = round(n, decimals);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  return `${sign}${fmtNum(Math.abs(rounded), decimals)}`;
}

/**
 * The one delta sentence every measured tile prints (client, 2026-09-16).
 *
 * The rail used to read `0 vs 2025` — the *absolute* change, which on a flat indicator is a
 * bare zero beside a year and says nothing at all. A change is a **percentage of what came
 * before**, so the line is `vs 2025: +16.2 %` wherever there is a previous value to divide
 * by; where the previous year is itself zero there is no percentage to report and the line
 * falls back to the absolute change in the tile's own unit; and where there is no earlier
 * value at all it says so rather than comparing against a null.
 */
function deltaLine(
  kpi: DashboardKpi,
  prevYear: number | null,
  t: ReturnType<typeof useTranslation>['t'],
): { text: string; direction: 'up' | 'down' | '' } {
  const { value, previous } = kpi;
  const measurable =
    prevYear !== null &&
    typeof value === 'number' &&
    Number.isFinite(value) &&
    typeof previous === 'number' &&
    Number.isFinite(previous);
  if (!measurable) return { text: t('dashboard.kpi.deltaNone'), direction: '' };
  const prev = previous as number;
  const now = value as number;
  if (prev === 0) {
    const change = now - prev;
    return {
      text: t('dashboard.kpi.deltaAbs', {
        year: prevYear,
        change: signed(change, kpi.fmt),
        unit: kpi.unit,
      }),
      direction: kpiDeltaClass(round(change, kpi.fmt)),
    };
  }
  const pct = ((now - prev) / Math.abs(prev)) * 100;
  // One decimal only while the move is small enough for it to matter: "+16.2 %" and
  // "+140 %" both read at a glance, "+140.3 %" does not.
  const decimals = Math.abs(pct) < 10 ? 1 : 0;
  return {
    text: t('dashboard.kpi.deltaPct', { year: prevYear, pct: signed(pct, decimals) }),
    // The colour follows the **printed** figure, not the raw one: a move that rounds to
    // "0 %" was reading as a green gain (review, 2026-09-16).
    direction: kpiDeltaClass(round(pct, decimals)),
  };
}

export function KpiTile({
  kpi,
  color,
  activeIdx,
  prevYear,
  onMap,
  index = 0,
  status,
  className,
}: KpiTileProps) {
  const { t } = useTranslation();
  const delta = deltaLine(kpi, prevYear, t);
  const pill = status ? <StatusPill status={status} /> : null;
  const cls = (extra: string) => ['kpi', extra, className].filter(Boolean).join(' ');
  /*
    The indicator's own colour, handed to CSS rather than painted here: the tile's 4 % ground
    tint, its delta ink and its hover border are all mixes of it, and mixing them in the
    stylesheet keeps one rule per effect instead of five inline styles per tile.
  */
  const tint = { '--kpi-color': color, '--i': index } as CSSProperties;

  /*
    An indicator with nothing but `demo:seed` values behind it is **unmeasured**, not
    achieved at zero (backend `3529d6c`; `provenance.is_active`, the same rule the
    Indicators pane and the map grey an indicator out by). The server already sends its
    value, previous, delta and whole spark as `null`; the tile keeps its place on the rail —
    the reader should see the indicator is awaiting results, not lose it — with an em dash
    where the number goes and the caption in place of a delta that would compare two nulls.
  */
  if (!kpi.active) {
    return (
      <div className={cls('kpi-awaiting')} data-testid={`kpi-${kpi.id}`} style={tint}>
        <div className="kdomain" style={{ background: color }} />
        {onMap && <MapButton id={kpi.id} onMap={onMap} />}
        {pill}
        <div className="klabel" title={kpi.label}>
          {kpi.label}
        </div>
        <div className="kval">
          <span className="kval-num">{NO_VALUE}</span>
          <small>{kpi.unit}</small>
        </div>
        {/* Row three, where a measured tile's delta and sparkline are — same baseline. */}
        <div className="kfoot">
          <div className="kawaiting">{t('dashboard.kpi.awaiting')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className={cls('')} data-testid={`kpi-${kpi.id}`} style={tint}>
      <div className="kdomain" style={{ background: color }} />
      {onMap && <MapButton id={kpi.id} onMap={onMap} />}
      {pill}
      <div className="klabel" title={kpi.label}>
        {kpi.label}
      </div>
      {/*
        Three rows, the same three on every tile (review, 2026-09-16): the label, the value,
        then the delta beside the sparkline. The grid owns the row heights, so a measured
        tile and one awaiting its first results print their numbers and their notes on
        exactly the same baselines — and the spark, in its own fixed 64x24 cell, can never
        sit on top of the delta text however narrow the tile gets.
      */}
      <div className="kval">
        <span className="kval-num">{fmtNum(kpi.value, kpi.fmt)}</span>
        <small>{kpi.unit}</small>
      </div>
      <div className="kfoot">
        <div className={['kdelta', delta.direction].filter(Boolean).join(' ')}>{delta.text}</div>
        <div className="kspark">
        <Spark
          series={kpi.spark}
          w={SPARK_W}
          h={SPARK_H}
          color={color}
          unit={kpi.unit}
          fmt={kpi.fmt}
          activeIdx={activeIdx}
          ariaLabel={kpi.label}
          areaGradient
        />
        </div>
      </div>
    </div>
  );
}

/**
 * §1 section I — the contractual hero tile.
 *
 * The same figure the compact tile prints, given the room the section's four indicators
 * deserve: a 28 px value, a soft gradient of the indicator's own colour along the top edge
 * (10 % → 0, so the accent is a light source rather than a second border), and the progress
 * ring against the commitment. Everything else — the label, the delta line, the sparkline,
 * the "View on map" glyph and the `kpi-<id>` test id — is the compact tile's, unchanged,
 * because the two tiles are the same indicator at two sizes and nothing may read differently
 * between them.
 *
 * The ring needs the *target row*, not the tile: `DashboardKpi.target` carries a percentage
 * but not the `status` or the `target_source` that say whether it is a real commitment, an
 * apportioned share, or a row still awaiting its first result.
 */
export interface HeroKpiTileProps
  extends Omit<KpiTileProps, 'status' | 'className'> {
  /**
   * The indicator's row in `targets[]`, when it has one. The ring is drawn from *this*, not
   * from `kpi.target`: a percentage alone cannot say whether the commitment is real, an
   * apportioned share, or a row still awaiting its first result — and a `no_target` row must
   * draw no ring at all rather than a ring at whatever `kpi.target` happens to carry.
   */
  target?: DashboardTargetRow;
}

export function HeroKpiTile({
  kpi,
  color,
  activeIdx,
  prevYear,
  onMap,
  index = 0,
  target,
}: HeroKpiTileProps) {
  const { t } = useTranslation();
  const delta = deltaLine(kpi, prevYear, t);
  const tint = { '--kpi-color': color, '--i': index } as CSSProperties;
  const awaiting = target?.status === 'awaiting';
  /* `no_target` has nothing to draw a ring of — and a ring at 0 % would invent a commitment
     the payload explicitly says does not exist. The row is the only source: falling back to
     `kpi.target.pct` here would draw exactly that invented ring on a `no_target` row whose
     tile still carries a stale percentage. */
  const pct = awaiting ? null : (target?.pct ?? null);
  const hasRing = awaiting || pct !== null;
  const measured = kpi.active && kpi.value !== null;

  return (
    <div
      className={`kpi kpi-hero${measured ? '' : ' kpi-awaiting'}`}
      data-testid={`kpi-${kpi.id}`}
      style={tint}
    >
      <div className="khero-accent" aria-hidden="true" />
      <div className="kdomain" style={{ background: color }} />
      {onMap && <MapButton id={kpi.id} onMap={onMap} />}
      <div className="klabel" title={kpi.label}>
        {kpi.label}
      </div>
      <div className="khero-main">
        <div className="kval">
          <span className="kval-num">{measured ? fmtNum(kpi.value, kpi.fmt) : NO_VALUE}</span>
          <small>{kpi.unit}</small>
        </div>
        {hasRing && (
          <ProgressRing
            pct={pct}
            color={color}
            awaiting={awaiting}
            caption={
              target?.target_source === 'apportioned'
                ? t('dashboard.ring.apportioned')
                : target?.target_source === 'allocated'
                  ? t('dashboard.ring.allocated')
                  : t('dashboard.ring.ofTarget')
            }
          />
        )}
      </div>
      <div className="kfoot">
        {measured ? (
          <>
            <div className={['kdelta', delta.direction].filter(Boolean).join(' ')}>{delta.text}</div>
            <div className="kspark">
              <Spark
                series={kpi.spark}
                w={SPARK_W}
                h={SPARK_H}
                color={color}
                unit={kpi.unit}
                fmt={kpi.fmt}
                activeIdx={activeIdx}
                ariaLabel={kpi.label}
                areaGradient
              />
            </div>
          </>
        ) : (
          <div className="kawaiting">{t('dashboard.kpi.awaiting')}</div>
        )}
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------- targets card */

export interface TargetsCardProps {
  payload: DashboardResponse;
  year: number;
  /**
   * The section's own KPI rail — the indicators that must each get a group, committed this
   * year or not (`targetGroups`). Defaults to `payload.kpis`, which is what a card rendered
   * from an un-narrowed payload should show.
   */
  rail?: DashboardKpi[];
  /**
   * The indicator's own colour, by indicator id — the page's `domainColor` through the KPI
   * rail (`features/dashboard/index.tsx`). Optional so the card can be rendered on its own;
   * without it every group falls back to the Overview's achieved green.
   */
  color?: (indicatorId: string) => string;
}

/**
 * A group's name, with its own code off the front of it if the server put it there.
 *
 * The live rail's own `kpi.label` already reads "B.2.1 Producers reached (ag. performance)" —
 * the code baked into the name, not a fact this card's fixture happened to carry. The row
 * prints the code once, in its own small badge; a label that repeats it verbatim right after
 * would be the one piece of this card a reader has to read twice. Checked by exact prefix
 * rather than assumed: a label that does *not* lead with its code (the component tests'
 * fixture, a future server shape) is printed whole, not guessed at.
 */
function labelWithoutCode(label: string, code: string): string {
  if (!label.startsWith(code)) return label;
  return label.slice(code.length).replace(/^[\s:–—-]+/, '');
}

/**
 * Section I — **one progress bar per contractual indicator** (client, 2026-10-08 feedback
 * round: *"Use progress bar chart"*, with a mock showing exactly this — a code, a name, one
 * bar, the achieved/target pair and the percentage).
 *
 * This replaces the 2026-09-16 round's grouped Achieved/Target bar-pair chart outright, not
 * just its skin: there is no axis, no legend, no hatch standing in for "this is the
 * commitment" — the bar's own unfilled length already says that, the same idiom Section II's
 * `.dash-outcome-bar` already uses one card at a time, applied here as a list instead of a
 * grid because the four rows share one card rather than four. `targetGroups` (`dashboard.ts`)
 * is untouched: it still decides the four indicators and their status, this only changed what
 * draws from it.
 *
 * `no_target` prints "No target set" and no bar; `awaiting` prints "awaiting first results"
 * and no bar — neither is a bar at zero, which would claim a measurement that was never made.
 */
export function TargetsCard({ payload, year, rail, color }: TargetsCardProps) {
  const { t } = useTranslation();

  /* One group per contractual indicator, whether or not the year carries a commitment for
     it — see `targetGroups`. The rail defaults to the payload's own KPIs so the card can
     still be rendered from a payload alone. */
  const shaped = targetGroups(rail ?? payload.kpis, payload.targets);
  const tint = (id: string) => color?.(id) ?? TARGET_COLORS.achieved;
  const apportioned = shaped.some((group) => group.apportioned);

  const exportCsv = () => {
    const rows = targetRows(payload);
    downloadCsv(
      `soilvalues_targets_${year}.csv`,
      toCsv(
        ['code', 'indicator', 'unit', 'achieved', 'target', 'pct', 'status'],
        payload.targets.map((row, i) => [
          row.code,
          row.label,
          row.unit,
          rows.series[1][i],
          rows.series[0][i],
          rows.pcts[i],
          row.status,
        ]),
      ),
    );
  };

  return (
    <div className="card" data-testid="card-targets">
      <CardHead
        title={t('dashboard.cards.targets', { year })}
        actions={
          <button type="button" className="btn ghost sm" data-testid="targets-csv" onClick={exportCsv}>
            <Icon name="download" size={12} />
            {t('dashboard.csv')}
          </button>
        }
      />
      <ul className="dash-targets-list" data-testid="targets-chart">
        {shaped.map((group) => {
          const hue = tint(group.id);
          const note =
            group.status === 'measured'
              ? null
              : group.status === 'awaiting'
                ? t('dashboard.kpi.awaiting')
                : t('dashboard.noTarget');
          return (
            <li key={group.id} className="dash-targets-item" data-barsg-group={group.id}>
              <p className="dash-targets-label">
                <span className="dash-targets-code" data-barsg-label={group.id}>
                  {group.code}
                  {group.apportioned ? '†' : ''}
                </span>
                {labelWithoutCode(group.label, group.code)}
              </p>
              {group.pct === null ? (
                <p className="dash-targets-note" data-barsg-note={group.id}>
                  {note}
                </p>
              ) : (
                <div className="dash-targets-row">
                  <div
                    className="dash-targets-bar"
                    role="img"
                    aria-label={`${group.code} ${fmtPct(group.pct, 0, false)}, ${fmtNum(group.achieved, 0)} / ${fmtNum(group.target, 0)} ${group.unit}`}
                  >
                    <span
                      data-barsg-bar="achieved"
                      style={{ width: `${Math.max(0, Math.min(100, group.pct))}%`, background: hue }}
                    />
                  </div>
                  <span className="dash-targets-figure" data-barsg-pct={group.id}>
                    {fmtNum(group.achieved, 0)} / {fmtNum(group.target, 0)} {group.unit} →{' '}
                    {fmtPct(group.pct, 0, false)}
                  </span>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {apportioned ? (
        <p className="dash-targets-foot" data-testid="targets-legend">
          {t('dashboard.targets.apportionedNote')}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------- II. one card per outcome */

/**
 * Section II — the outcome indicators as cards (client, 2026-09-16 round 3).
 *
 * Seven indicators, seven cards, whether or not the rail carries a figure for them:
 * `outcomeCards` builds the list off `targets[]` so an indicator awaiting its first result
 * is *present and saying so* rather than absent. Each card is the one sentence the section
 * owes the reader — what the indicator is, whether it has been measured, the figure, and how
 * far it is against its commitment — and nothing else; the row-by-row table that used to
 * repeat all of it underneath is gone.
 *
 * The progress bar is drawn only where a commitment exists. "No target set" is printed
 * quietly in its place, never a bar at zero.
 */
export function OutcomeCards({
  cards,
  color,
  activeIdx,
  onMap,
}: {
  cards: DashboardOutcomeCard[];
  /** The indicator's domain colour, as the page's `domainColor` answers it. */
  color: (domain: string | null) => string;
  /** The selected year's index in the Dashboard years (`DASHBOARD_YEARS`, 2024 on), for the sparkline's filled dot. */
  activeIdx: number;
  onMap?: (indicatorId: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <div className="dash-outcome-grid" data-testid="card-outcomes">
      {cards.map((card, i) => {
        const hue = color(card.domain);
        const measured = card.status === 'measured';
        const pct = card.pct;
        const style = { '--kpi-color': hue, '--i': i } as CSSProperties;
        return (
          <div
            key={card.id}
            className={`card dash-outcome${measured ? '' : ' dash-outcome-awaiting'}`}
            data-testid={`outcome-card-${card.id}`}
            style={style}
          >
            <div className="kdomain" style={{ background: hue }} />
            <div className="dash-outcome-head">
              {card.code ? <span className="dash-outcome-code">{card.code}</span> : null}
              <StatusPill status={card.status} />
              {onMap && (
                <MapButton
                  id={card.id}
                  onMap={onMap}
                  className="dash-outcome-map"
                  testId="target-map"
                />
              )}
            </div>
            <div className="dash-outcome-name" title={card.label}>
              {card.label}
            </div>
            <div className="kval">
              <span className="kval-num">
                {measured ? fmtNum(card.value, card.fmt) : NO_VALUE}
              </span>
              <small>{card.unit}</small>
            </div>
            {card.target === null ? (
              <div className="dash-outcome-note">{t('dashboard.noTarget')}</div>
            ) : (
              <div className="dash-outcome-target">
                <div className="dash-outcome-bar">
                  <span
                    style={{
                      width: `${Math.max(0, Math.min(100, pct ?? 0))}%`,
                      background: hue,
                    }}
                  />
                </div>
                <div className="dash-outcome-note">
                  {t('dashboard.outcome.ofTarget', {
                    pct: fmtNum(pct ?? 0, 0),
                    target: fmtNum(card.target, 0),
                    unit: card.unit,
                  })}
                </div>
              </div>
            )}
            {card.spark && measured ? (
              <div className="kspark">
                <Spark
                  series={card.spark}
                  w={SPARK_W}
                  h={SPARK_H}
                  color={hue}
                  unit={card.unit}
                  fmt={card.fmt}
                  activeIdx={activeIdx}
                  ariaLabel={card.label}
                  areaGradient
                />
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* --------------------------------------------------- what the targets say (row D) */

/**
 * The server's target sentences, one line per indicator, full width.
 *
 * Empty at country scope — the server sends `insights: []` there, because a target sentence
 * is a statement about a programme commitment and a country has none; the card renders
 * nothing at all rather than a heading over an empty list.
 *
 * `summariseInsights` does the collapsing (see its own comment): the six identical "awaiting
 * first results" sentences become one line naming their codes, and each measured
 * indicator's several sentences join onto one line. The payload is untouched.
 */
export function InsightsCard({ payload }: { payload: DashboardResponse }) {
  const { t } = useTranslation();
  const summary = summariseInsights(payload);
  /*
    §1 — a disclosure rather than a card that is always open (client, 2026-09-16). The
    sentences are the *commentary* on section I, not its figures, so below 1180 px — where
    the section is already two screens of tiles and charts — they start folded and the
    reader opens them. At desktop width there is room for them and they open with the page.

    The query is read **in the initialiser**, not in an effect: an effect runs after the
    first paint, so on a phone the disclosure opened, painted its list and then collapsed —
    a flash of content the reader did not ask for. Where there is no `matchMedia` at all
    (jsdom, server) it starts open, because a folded-away list is worse than a long one.
  */
  const [open, setOpen] = useState(
    () =>
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function' ||
      window.matchMedia('(min-width: 1180px)').matches,
  );

  if (summary.lines.length === 0 && summary.awaitingCodes.length === 0) return null;

  return (
    <details
      className="card dash-narrative"
      data-testid="card-insights"
      open={open}
      /* The reader's own click is the authority once the page is up: `open` is a controlled
         attribute here, so without this the next render would snap the disclosure back to
         whatever the media query decided at mount. */
      onToggle={(event) => setOpen(event.currentTarget.open)}
    >
      <summary className="dash-narrative-head">
        <div className="card-head-text">
          <h3>{t('dashboard.insights.title')}</h3>
          <div className="sub">{t('dashboard.insights.programmeLevel')}</div>
        </div>
        <Icon name="chev" size={14} />
      </summary>
      <ul className="dash-insights" data-testid="dash-insights">
        {/*
          The React key is the position, not `insight.key`: the server reuses one key
          (`targetProgress`, `targetEop`, …) for every indicator it makes that statement
          about, so the keys are not unique within one payload. `data-insight` still
          carries the server's own key, which is what §F7 asks the list to expose.
        */}
        {summary.lines.map((line, i) => (
          <li key={`${line.key}-${i}`} data-insight={line.key}>
            {line.text}
          </li>
        ))}
        {summary.awaitingCodes.length > 0 && (
          <li data-insight="awaiting" className="dash-insight-awaiting">
            {t('dashboard.insights.awaitingLine', {
              count: summary.awaitingCodes.length,
              codes: summary.awaitingCodes.join(', '),
            })}
          </li>
        )}
      </ul>
    </details>
  );
}

/* ---------------------------------------------------------------------- trend card */

/**
 * The cross-country title (client, 2026-10-08 feedback round): *"Replace 'Cross-country
 * trend — B.2.1 Producers reached (ag. performance) / 2021–2026, by country' with 'B.2.1
 * achievement by country'"* — one short title, the indicator's own code, no date range (the
 * years are the x axis already). The single-country variant the client did not address keeps
 * its existing title (`trendCountry`/`trendTitle`) but loses the same date-range subtitle,
 * for the same reason: a reader does not need the years named twice, once in words and once
 * as every tick on the chart under them.
 */
export function TrendCard({ payload }: { payload: DashboardResponse }) {
  const { t } = useTranslation();
  const { trend } = payload;
  const series = trendSeries(payload);
  const cross = series.length > 1;
  const code = payload.kpis.find((kpi) => kpi.id === trend.indicator)?.code ?? null;
  const title =
    cross && code
      ? t('dashboard.cards.trendTitleCross', { code })
      : t('dashboard.cards.trendTitle', {
          title: cross
            ? t('dashboard.cards.trendCross')
            : t('dashboard.cards.trendCountry', { country: series[0]?.name ?? NO_VALUE }),
          indicator: trend.label,
        });

  const exportCsv = () => {
    downloadCsv(
      `soilvalues_trend_${trend.indicator}.csv`,
      toCsv(
        ['country', 'indicator', 'unit', ...trend.years.map(String)],
        trend.series.map((row) => [row.name, trend.label, trend.unit, ...row.values]),
      ),
    );
  };

  return (
    <div className="card" data-testid="card-trend">
      <CardHead
        title={title}
        actions={
          <button type="button" className="btn ghost sm" data-testid="trend-csv" onClick={exportCsv}>
            <Icon name="download" size={12} />
            {t('dashboard.csv')}
          </button>
        }
      />
      {/*
        `dash-trend-chart`: the targets card beside this one is a nine-row list under a
        chart, so the grid stretches this card to a height its own chart does not need. The
        class centres the chart in what it is given rather than leaving one tall void below.
      */}
      <ChartFrame
        title={title}
        xLabel={t('charts.axis.year')}
        yLabel={axisTitle(t('dashboard.axis.trendValue'), trend.unit)}
        testId="trend-chart"
      >
        {(size) => (
          <LineChart
            series={series}
            labels={trend.years}
            unit={trend.unit}
            fmt={trend.fmt}
            w={chartW(size, TREND_CHART_W)}
            h={chartH(size, TREND_CHART_H)}
            /* Always from zero (approved design): this card's reader is weighing a country's
               absolute progress against a programme commitment, and an axis starting at the
               series' own floor would print a 5 % climb as a doubling. */
            zeroBaseline={1}
            /* The wash under the line, but only on a **one-country** trend (round 2 of the
               re-skin): four countries washed at once stack four translucent greens, blues and
               plums into one lavender-grey column, which is the muddying `areaFill`'s own doc
               warns about — and on this fixture, where every series has two points at the right
               edge, it printed as a smudge beside the axis. One line, one wash. */
            areaFill={!cross}
            ariaLabel={trend.label}
            className="dash-trend-chart"
          />
        )}
      </ChartFrame>
    </div>
  );
}
